import { Loader2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useUpdateProfile } from "@/lib/api";
import { apiErrorMessage, inlineFailure } from "@/lib/api-errors";

export function ProfileForm({
  initialFirstName,
  initialLastName,
}: {
  initialFirstName: string;
  initialLastName: string;
}) {
  const { t } = useTranslation();
  const [firstName, setFirstName] = useState(initialFirstName);
  const [lastName, setLastName] = useState(initialLastName);
  // The outcome of the last save, worded at render so a language switch re-words it.
  const [outcome, setOutcome] = useState<{ saved: true } | { error: unknown } | null>(null);
  const updateMutation = useUpdateProfile();

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setOutcome(null);
    try {
      await updateMutation.mutateAsync({ firstName, lastName });
      setOutcome({ saved: true });
    } catch (err) {
      setOutcome(inlineFailure({ error: err }));
    }
  };

  const message = outcome && "saved" in outcome ? t("settings.nameSaved") : "";
  const error =
    outcome && "error" in outcome ? apiErrorMessage(outcome.error, "updateProfile") : "";

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("settings.nameTitle")}</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSave} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label htmlFor="firstName" className="text-foreground text-sm font-medium">
                {t("settings.firstName")}
              </label>
              <Input
                id="firstName"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="lastName" className="text-foreground text-sm font-medium">
                {t("settings.lastName")}
              </label>
              <Input id="lastName" value={lastName} onChange={(e) => setLastName(e.target.value)} />
            </div>
          </div>
          {message && <p className="text-success text-sm">{message}</p>}
          {error && <p className="text-destructive text-sm">{error}</p>}
          <Button type="submit" disabled={updateMutation.isPending}>
            {updateMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              t("common.save")
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
